import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
} from 'react-native';
import {
  Card,
  TextInput,
  Button,
  Chip,
  Title,
  Paragraph,
  Divider,
  Searchbar,
  Menu,
  Provider,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';

interface BarsFilterPanelProps {
  filters: {
    search: string;
    region: string;
    venueType: string;
    minRating: number;
    priceTier: string;
    hasWebsite: boolean;
    hasPhone: boolean;
  };
  onFiltersChange: (filters: any) => void;
  onClearFilters: () => void;
  venueTypes: string[];
  regions: string[];
  priceTiers: string[];
  isVisible: boolean;
  onToggleVisibility: () => void;
}

const BarsFilterPanel: React.FC<BarsFilterPanelProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  venueTypes,
  regions,
  priceTiers,
  isVisible,
  onToggleVisibility,
}) => {
  const { themeData } = useTheme();
  const [searchQuery, setSearchQuery] = useState(filters.search);
  const [regionMenuVisible, setRegionMenuVisible] = useState(false);
  const [typeMenuVisible, setTypeMenuVisible] = useState(false);
  const [priceMenuVisible, setPriceMenuVisible] = useState(false);

  useEffect(() => {
    setSearchQuery(filters.search);
  }, [filters.search]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    onFiltersChange({ ...filters, search: query });
  };

  const handleRegionSelect = (region: string) => {
    setRegionMenuVisible(false);
    onFiltersChange({ ...filters, region });
  };

  const handleTypeSelect = (venueType: string) => {
    setTypeMenuVisible(false);
    onFiltersChange({ ...filters, venueType });
  };

  const handlePriceSelect = (priceTier: string) => {
    setPriceMenuVisible(false);
    onFiltersChange({ ...filters, priceTier });
  };

  const handleRatingChange = (minRating: number) => {
    onFiltersChange({ ...filters, minRating });
  };

  const handleToggleWebsite = () => {
    onFiltersChange({ ...filters, hasWebsite: !filters.hasWebsite });
  };

  const handleTogglePhone = () => {
    onFiltersChange({ ...filters, hasPhone: !filters.hasPhone });
  };

  const getTypeLabel = (venueType: string) => {
    switch (venueType) {
      case 'tiki_bar':
        return '🌺 Tiki Bar';
      case 'craft_cocktail_bar':
        return '🍸 Craft Cocktail Bar';
      case 'lounge':
        return '🥂 Lounge';
      case 'bar':
        return '🍺 Bar';
      case 'liquor_store':
        return '🏪 Liquor Store';
      default:
        return venueType.replace('_', ' ').toUpperCase();
    }
  };

  const getRegionLabel = (region: string) => {
    if (!region) return 'All Regions';
    return region;
  };

  const getPriceLabel = (priceTier: string) => {
    if (!priceTier) return 'All Prices';
    return priceTier;
  };

  const hasActiveFilters = () => {
    return filters.search || 
           filters.region || 
           filters.venueType || 
           filters.minRating > 0 || 
           filters.priceTier || 
           filters.hasWebsite || 
           filters.hasPhone;
  };

  if (!isVisible) {
    return (
      <View style={styles.collapsedContainer}>
        <Button
          mode="outlined"
          onPress={onToggleVisibility}
          icon="filter-variant"
          style={[styles.toggleButton, { borderColor: themeData.colors.primary }]}
          labelStyle={{ color: themeData.colors.primary }}
        >
          {hasActiveFilters() ? 'Filters Active' : 'Show Filters'}
        </Button>
      </View>
    );
  }

  return (
    <Provider>
      <Card style={[styles.filterCard, { backgroundColor: themeData.colors.cardBackground }]}>
        <Card.Content>
          <View style={styles.header}>
            <Title style={[styles.title, { color: themeData.colors.text }]}>
              <Ionicons name="filter" size={20} color={themeData.colors.primary} />
              {' '}Filters
            </Title>
            <Button
              mode="text"
              onPress={onToggleVisibility}
              icon="close"
              compact
            >
              Close
            </Button>
          </View>

          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
            {/* Search */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                🔍 Search
              </Paragraph>
              <Searchbar
                placeholder="Search by name or location..."
                onChangeText={handleSearchChange}
                value={searchQuery}
                style={[styles.searchBar, { backgroundColor: themeData.colors.surface }]}
                iconColor={themeData.colors.primary}
                inputStyle={{ color: themeData.colors.text }}
              />
            </View>

            <Divider style={styles.divider} />

            {/* Region Filter */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                📍 Location
              </Paragraph>
              <Menu
                visible={regionMenuVisible}
                onDismiss={() => setRegionMenuVisible(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setRegionMenuVisible(true)}
                    icon="map-marker"
                    style={styles.menuButton}
                  >
                    {getRegionLabel(filters.region)}
                  </Button>
                }
              >
                <Menu.Item
                  onPress={() => handleRegionSelect('')}
                  title="All Regions"
                />
                {regions.map((region) => (
                  <Menu.Item
                    key={region}
                    onPress={() => handleRegionSelect(region)}
                    title={region}
                  />
                ))}
              </Menu>
            </View>

            <Divider style={styles.divider} />

            {/* Venue Type Filter */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                🏪 Venue Type
              </Paragraph>
              <Menu
                visible={typeMenuVisible}
                onDismiss={() => setTypeMenuVisible(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setTypeMenuVisible(true)}
                    icon="store"
                    style={styles.menuButton}
                  >
                    {filters.venueType ? getTypeLabel(filters.venueType) : 'All Types'}
                  </Button>
                }
              >
                <Menu.Item
                  onPress={() => handleTypeSelect('')}
                  title="All Types"
                />
                {venueTypes.map((type) => (
                  <Menu.Item
                    key={type}
                    onPress={() => handleTypeSelect(type)}
                    title={getTypeLabel(type)}
                  />
                ))}
              </Menu>
            </View>

            <Divider style={styles.divider} />

            {/* Rating Filter */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                ⭐ Minimum Rating
              </Paragraph>
              <View style={styles.ratingContainer}>
                {[0, 3, 3.5, 4, 4.5].map((rating) => (
                  <Chip
                    key={rating}
                    selected={filters.minRating === rating}
                    onPress={() => handleRatingChange(rating)}
                    style={[
                      styles.ratingChip,
                      filters.minRating === rating && { backgroundColor: themeData.colors.primary }
                    ]}
                    textStyle={[
                      styles.ratingChipText,
                      filters.minRating === rating && { color: '#FFFFFF' }
                    ]}
                  >
                    {rating === 0 ? 'Any' : `${rating}+`}
                  </Chip>
                ))}
              </View>
            </View>

            <Divider style={styles.divider} />

            {/* Price Filter */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                💰 Price Tier
              </Paragraph>
              <Menu
                visible={priceMenuVisible}
                onDismiss={() => setPriceMenuVisible(false)}
                anchor={
                  <Button
                    mode="outlined"
                    onPress={() => setPriceMenuVisible(true)}
                    icon="currency-usd"
                    style={styles.menuButton}
                  >
                    {getPriceLabel(filters.priceTier)}
                  </Button>
                }
              >
                <Menu.Item
                  onPress={() => handlePriceSelect('')}
                  title="All Prices"
                />
                {priceTiers.map((tier) => (
                  <Menu.Item
                    key={tier}
                    onPress={() => handlePriceSelect(tier)}
                    title={tier}
                  />
                ))}
              </Menu>
            </View>

            <Divider style={styles.divider} />

            {/* Amenities Filters */}
            <View style={styles.section}>
              <Paragraph style={[styles.sectionTitle, { color: themeData.colors.text }]}>
                📞 Amenities
              </Paragraph>
              <View style={styles.amenitiesContainer}>
                <Chip
                  selected={filters.hasWebsite}
                  onPress={handleToggleWebsite}
                  style={[
                    styles.amenityChip,
                    filters.hasWebsite && { backgroundColor: themeData.colors.primary }
                  ]}
                  textStyle={[
                    styles.amenityChipText,
                    filters.hasWebsite && { color: '#FFFFFF' }
                  ]}
                >
                  🌐 Has Website
                </Chip>
                <Chip
                  selected={filters.hasPhone}
                  onPress={handleTogglePhone}
                  style={[
                    styles.amenityChip,
                    filters.hasPhone && { backgroundColor: themeData.colors.primary }
                  ]}
                  textStyle={[
                    styles.amenityChipText,
                    filters.hasPhone && { color: '#FFFFFF' }
                  ]}
                >
                  📞 Has Phone
                </Chip>
              </View>
            </View>

            {/* Clear Filters */}
            {hasActiveFilters() && (
              <View style={styles.clearSection}>
                <Button
                  mode="contained"
                  onPress={onClearFilters}
                  icon="close-circle"
                  style={styles.clearButton}
                >
                  Clear All Filters
                </Button>
              </View>
            )}
          </ScrollView>
        </Card.Content>
      </Card>
    </Provider>
  );
};

const styles = StyleSheet.create({
  collapsedContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  toggleButton: {
    borderRadius: 8,
  },
  filterCard: {
    margin: 16,
    borderRadius: 12,
    elevation: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  scrollView: {
    maxHeight: 500,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  searchBar: {
    borderRadius: 8,
    elevation: 2,
  },
  menuButton: {
    borderRadius: 8,
    justifyContent: 'flex-start',
  },
  ratingContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  ratingChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  ratingChipText: {
    fontSize: 12,
  },
  amenitiesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  amenityChip: {
    marginRight: 8,
    marginBottom: 8,
  },
  amenityChipText: {
    fontSize: 12,
  },
  divider: {
    marginVertical: 8,
  },
  clearSection: {
    marginTop: 16,
    alignItems: 'center',
  },
  clearButton: {
    borderRadius: 8,
  },
});

export default BarsFilterPanel;
